import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatMap, map } from 'rxjs/operators';
import {
  mojoNotesArchiveSuccess,
  mojoNotesLoad,
  mojoNotesLoadSuccess,
  mojoNotesReset,
  notesLoad,
} from 'mojo-irp-notes';
import { withLatestFrom } from 'rxjs';
import { Store } from '@ngrx/store';
import { NotesFiltersInterface } from 'mojo-irp-notes/lib/models/notes-filters';
import { getNotesState } from './notes.selectors';
import { fastrNotesLoad } from './notes.actions';
import { getLoggedInUser } from '../users/users.selectors';
import { loadUsersByIds } from '../users/users.actions';
import { SnackBarService } from '@app/shared/services/snack-bar.service';

const DEFAULT_NOTE_FILTERS = {
  isForHiringTeamTab: false,
  isForRecruitingTeamTab: false,
  systemGenerated: true,
  userGenerated: true,
  searchPhrases: [],
};

@Injectable()
export class NotesEffects {
  private actions$ = inject(Actions);
  private store = inject(Store);
  private snackBarService = inject(SnackBarService);

  pluginLoad$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fastrNotesLoad),
      map(() => {
        return notesLoad();
      }),
    ),
  );

  deleteNote$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(mojoNotesArchiveSuccess),
        map(() => this.snackBarService.openSnackBar('Note deleted.')),
      ),
    { dispatch: false },
  );

  load$ = createEffect(() =>
    this.actions$.pipe(
      ofType(notesLoad),
      withLatestFrom(this.store.select(getNotesState), this.store.select(getLoggedInUser)),
      concatMap(([, { subjectId, subjectType, pageNumber }, loggedInUser]) => {
        const notesFilters: NotesFiltersInterface = {
          ...DEFAULT_NOTE_FILTERS,
          pageNumber,
          pageSize: 20,
          subjectId: 0,
          entityExternalId: subjectId + '',
          id: subjectType?.toString() + '',
        };

        const actions = [];
        if (pageNumber === 0) {
          actions.push(mojoNotesReset());
        }

        return [
          ...actions,
          mojoNotesLoad({
            anonymised: false,
            loggedInUserId: loggedInUser ? loggedInUser.id : 0,
            notesFilters,
          }),
        ];
      }),
    ),
  );

  loadSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(mojoNotesLoadSuccess),
      map(({ userIds }) => {
        return loadUsersByIds({ userIds });
      }),
    ),
  );
}
